import { useContext, useMemo } from 'react'
import { Info } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTeam } from 'app/frontend/base/config/context/team'
import { usePageInfo, useTeamAuditData } from 'app/frontend/base/config/queries'
import { updateTierPriority } from 'app/frontend/base/api/character'
import { displayErrorToast, wowheadUrl } from 'app/frontend/base/helpers'
import { gameVersionHas } from 'app/frontend/types/custom/GameVersion'
import { useTranslation } from 'react-i18next'
import { LootContext } from '../context'
import { seasonWishlistMetadata, withSeasonWishlistMetadata } from '../helpers'
import { getSeasonData } from 'app/frontend/base/services/seasonData'
import ItemIcon from 'app/frontend/base/components/ItemIcon'
import Select from 'app/frontend/base/components/Select'
import Card from 'app/frontend/base/components/Card'
import { itemSlotLabels } from 'app/frontend/types/custom/ItemSlot'
import UpgradeTrack from 'app/frontend/types/custom/UpgradeTrack'
import { cn } from '@/lib/utils'
import {
  detectedPiece,
  isAutoObtained,
  priorityOptions,
  seasonTierItems,
  tierSlots,
  TierSlot,
} from './tierPriorityHelpers'

type Props = {
  characterId: number
  disabled?: boolean
}

const TierPriority = ({ characterId, disabled }: Props) => {
  const { team } = useTeam()
  const { selectedSeason } = useContext(LootContext)
  const {
    data: { gameVersion },
  } = usePageInfo()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: auditData, isPending: isAuditPending } = useTeamAuditData(
    team.settings.tierPriorityEnabled.value ? team.id : undefined,
  )

  const teamQueryKey = ['team', { path: team.path }]

  const member = team.members?.find(
    (m) => m.characterReference.id === characterId,
  )
  const character = member?.characterReference
  const storedPriority = seasonWishlistMetadata(
    character?.wishlistMetadata,
    selectedSeason.id,
  ).tier_priority

  const classTier = useMemo(() => {
    if (!character) return undefined
    return getSeasonData(gameVersion).find(
      (season) => season.id === selectedSeason.id,
    )?.tier_items_by_slot?.[String(character.classId)]
  }, [character, gameVersion, selectedSeason.id])

  const minimumTrack = team.settings.tierPriorityMinimumTrack
    .value as UpgradeTrack
  const tierItems = seasonTierItems(auditData?.[characterId], selectedSeason.id)

  const mutation = useMutation({
    mutationFn: (nextPriority: Record<string, string>) =>
      updateTierPriority(team.id, characterId, selectedSeason.id, nextPriority),
    onMutate: async (nextPriority) => {
      await queryClient.cancelQueries({ queryKey: teamQueryKey })
      const previousTeam = queryClient.getQueryData<wowaudit.Team>(teamQueryKey)

      queryClient.setQueryData<wowaudit.Team>(teamQueryKey, (old) =>
        old
          ? {
              ...old,
              members: old.members?.map((m) =>
                m.characterReference.id === characterId
                  ? {
                      ...m,
                      characterReference: {
                        ...m.characterReference,
                        wishlistMetadata: withSeasonWishlistMetadata(
                          m.characterReference.wishlistMetadata,
                          selectedSeason.id,
                          { tier_priority: nextPriority },
                        ),
                      },
                    }
                  : m,
              ),
            }
          : old,
      )

      return { previousTeam }
    },
    onError: (error, _nextPriority, context) => {
      if (context?.previousTeam) {
        queryClient.setQueryData(teamQueryKey, context.previousTeam)
      }
      displayErrorToast(error, t)
    },
    onSuccess: (newTeam) => {
      queryClient.setQueryData(teamQueryKey, newTeam)
    },
  })

  const setSlotPriority = (slot: TierSlot, value: string | undefined) => {
    if (disabled || mutation.isPending) return

    const next = { ...storedPriority }
    if (!value || value === 'unset') {
      delete next[slot]
    } else {
      next[slot] = value
    }
    mutation.mutate(next)
  }

  if (
    !team.settings.tierPriorityEnabled.value ||
    !gameVersionHas('loot', gameVersion) ||
    !character ||
    !classTier ||
    tierSlots.every((slot) => !classTier[slot])
  ) {
    return null
  }

  const count = tierSlots.filter(
    (slot) =>
      isAutoObtained(slot, tierItems, minimumTrack) ||
      storedPriority[slot] === 'obtained',
  ).length

  return (
    <div className='w-full max-w-full min-w-0 sm:w-auto sm:shrink-0'>
      <h4 className='flex items-center gap-2 mb-1'>
        Tier priority
        <span
          className={cn(
            'text-sm font-normal tabular-nums transition-opacity duration-300',
            isAuditPending ? 'opacity-0' : 'opacity-100',
            !isAuditPending && count >= 4
              ? 'text-success-foreground'
              : !isAuditPending && count >= 2
              ? 'text-warning-foreground'
              : 'text-muted-foreground',
          )}
        >
          {count} / {tierSlots.length}
        </span>
      </h4>
      <div className='flex flex-wrap gap-2'>
        {tierSlots.map((slot) => {
          const itemId = classTier[slot]
          if (!itemId) return null

          const autoObtained = isAutoObtained(slot, tierItems, minimumTrack)
          const value = autoObtained
            ? 'obtained'
            : storedPriority[slot] ?? 'unset'

          return (
            <Card key={slot} className='w-30 flex flex-col overflow-hidden p-0'>
              <a
                href={wowheadUrl(gameVersion, undefined, itemId)}
                target='_blank'
                rel='noreferrer'
                className='flex items-center gap-2 min-w-0 border-b hover:bg-accent transition-colors'
              >
                <ItemIcon id={itemId} className='w-9 h-9 shrink-0' />
                <span className='truncate text-sm text-center w-full font-medium pr-2'>
                  {itemSlotLabels[slot]}
                </span>
              </a>

              {isAuditPending ? (
                <div className='h-7 w-full animate-pulse bg-muted' />
              ) : autoObtained ? (
                <div className='flex items-center justify-center gap-2.5 text-xs text-success-foreground font-medium bg-success px-2 py-1.5'>
                  <span>Obtained</span>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className='h-3.5 w-3.5 text-muted-foreground' />
                    </TooltipTrigger>
                    <TooltipContent>
                      Found equipped, on the{' '}
                      <strong>{detectedPiece(slot, tierItems).track}</strong>{' '}
                      track
                    </TooltipContent>
                  </Tooltip>
                </div>
              ) : (
                <Select
                  value={value}
                  setValue={(next) => setSlotPriority(slot, next)}
                  options={priorityOptions}
                  disabled={disabled || mutation.isPending}
                  className={cn(
                    'bg-card h-auto justify-center rounded-none border-0 px-2 py-1.5 text-xs font-medium',
                    priorityOptions.find((o) => o.value === value)?.background,
                  )}
                />
              )}
            </Card>
          )
        })}
      </div>
    </div>
  )
}

export default TierPriority
